import React from 'react';
import { Users, Layers, Calendar, Heart } from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, UserMiniCard, StatusBadge } from './shared';
import { useUserHousehold } from '../../hooks/useUserTabs';
import { cn, formatDateTime, parseDateOnly } from '../../utils/helpers';

interface HouseholdTabProps {
  userId: number;
}

const HouseholdTab: React.FC<HouseholdTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserHousehold(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No household data available" />;

  const { members, combos } = data;

  if (members.length === 0 && combos.length === 0) {
    return <TabEmpty message="No household members or combos found" icon={Users} />;
  }

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          label="Household Members"
          value={members.length}
          icon={Users}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Combos"
          value={combos.length}
          icon={Layers}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
      </div>

      {/* Household Members */}
      {members.length > 0 && (
        <SectionCard title="Household Members" icon={Users} subtitle={`${members.length} member${members.length !== 1 ? 's' : ''}`}>
          <div className="space-y-2.5">
            {members.map((member) => {
              const subtitleParts: string[] = [];
              if (member.relationshipToOwner) {
                subtitleParts.push(member.relationshipToOwner);
              }
              if (member.dateOfBirth) {
                subtitleParts.push(`DOB: ${parseDateOnly(member.dateOfBirth).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
              }

              return (
                <UserMiniCard
                  key={member.id}
                  user={{
                    id: member.id,
                    name: member.name,
                    profileImage: member.profileImage,
                  }}
                  subtitle={subtitleParts.join(' \u00B7 ') || undefined}
                  badge={
                    member.relationshipToOwner ? (
                      <StatusBadge status={member.relationshipToOwner} variant="info" />
                    ) : undefined
                  }
                />
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Household Combos */}
      {combos.length > 0 && (
        <SectionCard title="Household Combos" icon={Layers} subtitle={`${combos.length} combo${combos.length !== 1 ? 's' : ''}`}>
          <div className="space-y-4">
            {combos.map((combo) => (
              <div
                key={combo.id}
                className="rounded-lg border border-border/50 bg-muted/5 overflow-hidden"
              >
                {/* Combo Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 bg-muted/10">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-violet-500/10">
                      <Heart className="h-3.5 w-3.5 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{combo.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {combo.members.length} member{combo.members.length !== 1 ? 's' : ''}
                        {combo.createdAt && (
                          <span> &middot; Created {formatDateTime(combo.createdAt)}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-mono">#{combo.id}</span>
                </div>

                {/* Combo Members */}
                {combo.members.length > 0 ? (
                  <div className="p-3 space-y-2">
                    {combo.members.map((cm) => (
                      <div
                        key={cm.id}
                        className="flex items-center gap-3 p-2 rounded-md bg-muted/5"
                      >
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {cm.member.profileImage ? (
                            <img src={cm.member.profileImage} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-[10px] font-semibold text-primary">
                              {(cm.member.name || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">
                            {cm.member.name || 'Unknown'}
                          </p>
                          {cm.member.relationshipToOwner && (
                            <p className="text-[10px] text-muted-foreground">
                              {cm.member.relationshipToOwner}
                            </p>
                          )}
                        </div>
                        <span className="text-[10px] text-muted-foreground font-mono">
                          #{cm.member.id}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-xs text-muted-foreground">No members in this combo</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default HouseholdTab;
